import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Clock, Loader2, AlertTriangle, HelpCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Message } from '@/types/message-types';
import { useLatestConversationRun } from '@/lib/run-observability/use-latest-conversation-run';
import { resolveAiAutoStatus, type AiAutoModeState, type AiAutoStatusDetail } from '@/lib/run-observability/ai-auto-status';

interface AIAutoModeStatusProps {
  messages: Message[];
  conversationId: string;
}

interface StatusPresentation {
  label: string;
  icon: typeof Bot;
  color: string;
  bgColor: string;
  borderColor: string;
  animated: boolean;
}

function presentationFor(
  state: Exclude<AiAutoModeState, 'empty'>,
  t: (key: string, opts?: Record<string, unknown>) => string,
): StatusPresentation {
  switch (state) {
    case 'processing':
      return {
        label: t('aiAutoGeneratingResponse'),
        icon: Loader2,
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900',
        borderColor: 'border-blue-200 dark:border-blue-700',
        animated: true,
      };
    case 'failed':
      return {
        label: t('aiAutoFailed'),
        icon: AlertTriangle,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-100 dark:bg-red-900',
        borderColor: 'border-red-200 dark:border-red-700',
        animated: false,
      };
    case 'unknown':
      return {
        label: t('aiAutoStatusUnknown'),
        icon: HelpCircle,
        color: 'text-yellow-700 dark:text-yellow-400',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900',
        borderColor: 'border-yellow-200 dark:border-yellow-700',
        animated: false,
      };
    case 'awaitingCustomer':
    default:
      return {
        label: t('aiAutoAwaitingCustomer'),
        icon: Clock,
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-900',
        borderColor: 'border-gray-200 dark:border-gray-700',
        animated: false,
      };
  }
}

/** Secondary line under the badge — the "what/when" that turns a status into something actionable. */
function detailFor(
  state: Exclude<AiAutoModeState, 'empty'>,
  detail: AiAutoStatusDetail,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | null {
  if (state === 'failed') {
    return detail.errorSummary
      ? t('aiAutoFailedDetail', { error: detail.errorSummary })
      : t('aiAutoFailedDetailNoSummary');
  }
  if (state === 'unknown') {
    if (detail.reason === 'staleRunning') return t('aiAutoUnknownStaleRunning');
    if (detail.reason === 'queryFailed') return t('aiAutoUnknownQueryFailed');
    return t('aiAutoUnknownNoRun');
  }
  return null;
}

/**
 * The AI Auto Mode badge. Historically this deduced its state purely from `messages[last]`'s
 * sender — "customer sent it" meant "Analyzing", forever, with no notion of the engine actually
 * failing or hanging. It now reads `public.workflow_run` (via useLatestConversationRun) for the
 * conversation's freshest engine run and reports what that run's status actually says, including
 * "failed" and "we can't tell" — see src/lib/run-observability/ai-auto-status.ts for the full
 * decision table and why each branch exists.
 *
 * Renders nothing at all for a conversation with zero messages (state 'empty') — a fresh
 * conversation, or one just reset via the operator's "Reset conversation" button, has nothing to
 * analyze, so there is nothing honest to say here: not "Analyzing", not "Ready", not "Unknown".
 */
export function AIAutoModeStatus({ messages, conversationId }: AIAutoModeStatusProps) {
  const { t } = useTranslation('common');
  // Before the first fetch resolves, latestRun is undefined; `?? null` routes that through the
  // same "no run recorded (yet)" branch as a conversation with no runs at all — never a guessed
  // "processing"/"awaiting" state, just whatever that branch's honest default is.
  const { data: latestRun, isError } = useLatestConversationRun(conversationId);

  const lastMessage = messages[messages.length - 1];
  const { state, detail } = resolveAiAutoStatus({
    latestRun: latestRun ?? null,
    runQueryFailed: isError,
    hasMessages: messages.length > 0,
    lastMessageFromCustomer: lastMessage?.sender_type === 'customer',
  });

  if (state === 'empty') return null;

  const presentation = presentationFor(state, t);
  const StatusIcon = presentation.icon;
  const detailText = detailFor(state, detail, t);

  return (
    <Card className="mx-4 mb-4 p-6 text-center">
      <div className="flex flex-col items-center space-y-4">
        {/* Main title */}
        <div className="flex items-center space-x-2">
          <Bot className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">
            {t('aiAutoMode')}
          </h3>
        </div>

        {/* Status indicator */}
        <Badge
          variant="secondary"
          className={`
            ${presentation.bgColor} ${presentation.borderColor}
            text-sm px-4 py-2 font-medium
            ${presentation.animated ? 'animate-pulse' : ''}
          `}
        >
          <StatusIcon
            className={`
              mr-2 h-4 w-4 ${presentation.color}
              ${presentation.animated ? 'animate-spin' : ''}
            `}
          />
          {presentation.label}
        </Badge>

        {detailText && (
          <p className="text-xs text-muted-foreground max-w-md">{detailText}</p>
        )}
      </div>
    </Card>
  );
}
